import React, { useState } from "react";
import {
  HiOutlineUser,
  HiOutlineShoppingBag,
  HiOutlineLogout,
  HiMenu,
  HiX,
} from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";
import Layout from "../../components/web/Layout";
import Profile from "../../components/user/Profile";
import OrderList from "../../components/user/order/OrderList";
import UserAuth from "../../middleware/UserAuth";
import usePaginate from "../../hooks/api/usePaginate";
import apiRequest from "../../utils/apiRequest";
import Container from "../../components/ui/container/Container";
import { useAuth } from "../../context/AuthContext";
import { useSelector } from "react-redux";

const MyAccount = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { handleLogout } = useAuth();
  const user = useSelector((state) => state.globalState.userProfile);
  const {
    currentPage,
    data,
    error,
    limit,
    loading,
    setPage,
    setSorting,
    sort,
    totalItems,
    fetchData,
    totalPages,
  } = usePaginate({
    apiMethod: apiRequest.getUserOrders,
    dataKey: "orders",
  });

  const tabs = [
    {
      id: "profile",
      label: "My Profile",
      icon: <HiOutlineUser className="w-5 h-5" />,
      description: "Update your personal information",
    },
    {
      id: "orders",
      label: "My Orders",
      icon: <HiOutlineShoppingBag className="w-5 h-5" />,
      description: "View and track your orders",
    },
  ];

  const toggleMobileNav = () => {
    setMobileNavOpen(!mobileNavOpen);
  };

  return (
    <Layout>
      <UserAuth>
        <Container className={"my-5"}>
          <div className="flex flex-col gap-6 md:flex-row">
            <div className="flex items-center justify-between pb-4 border-b md:hidden">
              <div>
                <h1 className="text-xl font-bold text-gray-800">My Account</h1>
                <p className="text-sm text-gray-500">
                  {tabs.find((t) => t.id === activeTab)?.description}
                </p>
              </div>
              <button
                onClick={toggleMobileNav}
                className="p-2 text-gray-500 rounded-md hover:bg-gray-100"
              >
                {mobileNavOpen ? (
                  <HiX className="w-5 h-5" />
                ) : (
                  <HiMenu className="w-5 h-5" />
                )}
              </button>
            </div>
            
            <div
              className={`${
                mobileNavOpen ? "block" : "hidden"
              } md:block w-full md:w-64 lg:w-72 shrink-0`}
            >
              <div className="p-4 bg-white rounded-lg shadow-sm md:sticky md:top-6">
                {/* User Info */}
                <div className="flex items-center mb-6 space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 font-medium bg-gray-200 rounded-full">
                    {user?.name?.charAt(0) || "U"}
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">
                      {user?.name || "User"}
                    </p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                  </div>
                </div>

                {/* Navigation */}
                <nav className="space-y-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setMobileNavOpen(false);
                      }}
                      className={`w-full text-left p-3 rounded-lg transition-colors duration-200 flex items-start space-x-3
                        ${
                          activeTab === tab.id
                            ? "bg-green-50 text-green-700"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                    >
                      <span
                        className={`mt-0.5 ${
                          activeTab === tab.id
                            ? "text-green-600"
                            : "text-gray-500"
                        }`}
                      >
                        {tab.icon}
                      </span>
                      <div>
                        <p className="font-medium">{tab.label}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {tab.description}
                        </p>
                      </div>
                    </button>
                  ))}

                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full p-3 space-x-3 text-left text-gray-700 transition-colors duration-200 rounded-lg hover:bg-gray-100"
                  >
                    <HiOutlineLogout className="w-5 h-5 text-gray-500" />
                    <span className="font-medium">Logout</span>
                  </button>
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  {activeTab === "profile" && <Profile />}
                  {activeTab === "orders" && (
                    <OrderList
                      currentPage={currentPage}
                      data={data}
                      error={error}
                      limit={limit}
                      loading={loading}
                      setPage={setPage}
                      setSorting={setSorting}
                      sort={sort}
                      totalItems={totalItems}
                      fetchData={fetchData}
                      totalPages={totalPages}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </Container>
      </UserAuth>
    </Layout>
  );
};

export default MyAccount;
